// store/modules/user.js
import { getUserFullPermissions } from '@/api/RoleManager'
import { login as loginApi } from '@/api/UserManager'
import { isRtuUser } from '@/utils/roles'

// ✅ 确保 state 正确初始化
const state = () => ({
  user: null,
  menus: [],
  permissions: [],
  permissionMap: {}
})

const mutations = {
  SET_USER(state, user) {
    // ✅ 确保 state 存在
    if (!state) {
      console.error('state is null in SET_USER')
      return
    }
    state.user = user || null
  },
  SET_MENUS(state, menus) {
    if (!state) {
      console.error('state is null in SET_MENUS')
      return
    }
    state.menus = menus || []
  },
  SET_PERMISSIONS(state, permissions) {
    if (!state) {
      console.error('state is null in SET_PERMISSIONS')
      return
    }
    state.permissions = permissions || []
    state.permissionMap = {}
    if (Array.isArray(state.permissions)) {
      state.permissions.forEach(p => {
        if (p) {
          state.permissionMap[p] = true
        }
      })
    }
  },
  CLEAR(state) {
    if (!state) return
    state.user = null
    state.menus = []
    state.permissions = []
    state.permissionMap = {}
  }
}

const actions = {
  // 登录
  async login({ commit }, loginData) {
    try {
      commit('CLEAR')
      sessionStorage.removeItem('authToken')
      sessionStorage.removeItem('roleId')
      sessionStorage.removeItem('user')
      sessionStorage.removeItem('menus')
      sessionStorage.removeItem('permissions')
      sessionStorage.removeItem('permissionMap')
      const res = await loginApi(loginData)
      console.log('登录API返回:', res)
      
      if (res && res.success) {
        const { user, menus, permissions } = res.data || {}
        // 服务端(dev/zzhang-fix)返回 accessToken 字段
        const token = res.data?.token || res.data?.accessToken
        
        console.log('开始存储数据...')
        console.log('user:', user)
        console.log('menus:', menus)
        console.log('permissions:', permissions)
        
        // 存储 Token
        if (token) {
          sessionStorage.setItem('authToken', token)
        }
        // 角色 ID：路由白名单与受限角色权限判定依赖它（见 utils/roles.js）
        if (res.data?.roleId !== undefined) {
          sessionStorage.setItem('roleId', String(res.data.roleId))
        }
        
        // ✅ 提交 mutations 前检查数据
        commit('SET_USER', user || null)
        commit('SET_MENUS', menus || [])
        commit('SET_PERMISSIONS', permissions || [])
        
        // 持久化存储
        try {
          if (user) {
            sessionStorage.setItem('user', JSON.stringify(user))
          }
          if (menus) {
            sessionStorage.setItem('menus', JSON.stringify(menus))
          }
          if (permissions) {
            sessionStorage.setItem('permissions', JSON.stringify(permissions))
   
              const permissionMap = {}
              permissions.forEach(p => {
                  if (p) {
                      permissionMap[p] = true
                  }
              })
              // ✅ 使用 JSON.stringify 序列化
              sessionStorage.setItem('permissionMap', JSON.stringify(permissionMap))
        
          }
        } catch (storageError) {
          console.error('存储数据到 sessionStorage 失败:', storageError)
        }
        
        console.log('数据存储完成')
        return res
      }
      return res
    } catch (error) {
      console.error('登录 action 失败:', error)
      return { success: false, message: error.message }
    }
  },
  
  // 从缓存恢复权限数据
  restore({ commit }) {
    try {
      const user = sessionStorage.getItem('user')
      const menus = sessionStorage.getItem('menus')
      const permissions = sessionStorage.getItem('permissions')
      
      console.log('从 sessionStorage 恢复数据...')
      console.log('user:', user)
      console.log('menus:', menus)
      console.log('permissions:', permissions)
      
      if (user) {
        try {
          commit('SET_USER', JSON.parse(user))
        } catch (e) {
          console.error('解析用户数据失败:', e)
        }
      }
      if (menus) {
        try {
          commit('SET_MENUS', JSON.parse(menus))
        } catch (e) {
          console.error('解析菜单数据失败:', e)
        }
      }
      if (permissions) {
        try {
          commit('SET_PERMISSIONS', JSON.parse(permissions))
        } catch (e) {
          console.error('解析权限数据失败:', e)
        }
      }
    } catch (error) {
      console.error('恢复数据失败:', error)
    }
  },
  
  // 刷新权限
  async refreshPermissions({ commit }, lang = 'zh-CN') {
    try {
      const res = await getUserFullPermissions(lang)
      if (res && res.success) {
        const { user, menus, permissions } = res.data || {}
        commit('SET_USER', user || null)
        commit('SET_MENUS', menus || [])
        commit('SET_PERMISSIONS', permissions || [])
        
        if (user) {
          sessionStorage.setItem('user', JSON.stringify(user))
        }
        if (menus) {
          sessionStorage.setItem('menus', JSON.stringify(menus))
        }
        if (permissions) {
          sessionStorage.setItem('permissions', JSON.stringify(permissions))
        }
      }
      return res
    } catch (error) {
      console.error('刷新权限失败:', error)
      return { success: false, message: error.message }
    }
  },
  
  // 登出
  logout({ commit }) {
    commit('CLEAR')
    sessionStorage.removeItem('authToken')
    sessionStorage.removeItem('roleId')
    sessionStorage.removeItem('user')
    sessionStorage.removeItem('menus')
    sessionStorage.removeItem('permissions')
    sessionStorage.removeItem('permissionMap')
    
  }
}

// local-test：dev/zzhang-fix 服务端未实现 user-full-permissions 接口，
// 权限数据永远为空 —— 为空时放行（与旧版部署行为一致），接口上线后删除此判断。
// 受限角色（RTU User）例外：fail-closed（见 utils/roles.js）。
const isPermissionMapEmpty = (state) =>
  !isRtuUser() &&
  (!state || !state.permissionMap || Object.keys(state.permissionMap).length === 0)

const getters = {
  hasPermission: (state) => (permission) => {
    if (!permission) return true
    if (isPermissionMapEmpty(state)) return true
    return state.permissionMap[permission] === true
  },
  hasAnyPermission: (state) => (permissions) => {
    if (!permissions || permissions.length === 0) return true
    if (isPermissionMapEmpty(state)) return true
    return permissions.some(p => state.permissionMap[p] === true)
  },
  hasAllPermissions: (state) => (permissions) => {
    if (!permissions || permissions.length === 0) return true
    if (isPermissionMapEmpty(state)) return true
    return permissions.every(p => state.permissionMap[p] === true)
  }
}

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters
}